import { FC, useState } from 'react';

import { ExtraPaymentDto } from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  GiftCouponForRedemptionDashboard,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';

import { CsMemoCouponCell } from './csMemo';
import { ExtraFulfillModal, ExtraPayModal } from './ExtraPaymentActionModals';

const fmtTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${m}.${day} ${h}:${min}`;
};

const fmtPhone = (phone: string | undefined) => {
  if (!phone) return '-';
  const n = phone.replace(/[^0-9]/g, '');
  if (n.length === 11) return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
  return phone;
};

const productLabel = (c: GiftCouponForRedemptionDashboard): string => {
  const itemName =
    typeof c.giftItem === 'object'
      ? c.giftItem?.name || c.giftItem?.giftItemInfo?.name || '-'
      : '-';
  const unitName =
    c.selectedChoiceUnit && typeof c.selectedChoiceUnit === 'object'
      ? c.selectedChoiceUnit.name
      : '';
  return unitName ? `${itemName} - ${unitName}` : itemName;
};

interface ExtraPaymentTabProps {
  items: GiftCouponForRedemptionDashboard[];
  onPay: (couponId: string, dto: ExtraPaymentDto) => Promise<boolean>;
  onFulfill: (couponId: string, donorCouponId: string) => Promise<boolean>;
  onCancel: (couponId: string) => Promise<boolean>;
  getDonors: (couponId: string) => Promise<GiftCouponSummary[]>;
}

export const ExtraPaymentTab: FC<ExtraPaymentTabProps> = ({
  items,
  onPay,
  onFulfill,
  onCancel,
  getDonors,
}) => {
  const [payTarget, setPayTarget] = useState<GiftCouponForRedemptionDashboard | null>(
    null,
  );
  const [fulfillTarget, setFulfillTarget] =
    useState<GiftCouponForRedemptionDashboard | null>(null);
  const [cancelingId, setCancelingId] = useState<string | null>(null);

  const handleCancel = async (couponId: string) => {
    setCancelingId(couponId);
    try {
      await onCancel(couponId);
    } finally {
      setCancelingId(null);
    }
  };

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white py-12 text-center text-sm text-gray-400">
        추가결제 대기 중인 건이 없습니다.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border bg-white">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-gray-500">
          <tr>
            <th className="whitespace-nowrap px-4 py-2 font-medium">상품</th>
            <th className="whitespace-nowrap px-4 py-2 font-medium">코드</th>
            <th className="whitespace-nowrap px-4 py-2 font-medium">신청자</th>
            <th className="whitespace-nowrap px-4 py-2 font-medium">신청일시</th>
            <th
              className="whitespace-nowrap px-4 py-2 font-medium"
              title="CS 응대 메모 (클릭 시 메모장)"
            >
              CS메모
            </th>
            <th className="whitespace-nowrap px-4 py-2 font-medium">처리</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((c) => (
            <tr key={c._id} className="hover:bg-gray-50">
              <td className="px-4 py-2.5 text-gray-800">{productLabel(c)}</td>
              <td className="whitespace-nowrap px-4 py-2.5 font-mono text-gray-600">
                {c.fullCode || c.code}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-gray-700">
                {c.recipient?.name && <span>{c.recipient.name} </span>}
                <span className="text-gray-500">
                  {fmtPhone(c.recipient?.phoneNumber)}
                </span>
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">
                {fmtTime(c.recipient?.requestedAt)}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5">
                <CsMemoCouponCell coupon={c} />
              </td>
              <td className="whitespace-nowrap px-4 py-2.5">
                <div className="flex gap-2">
                  <button
                    onClick={() => setPayTarget(c)}
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-700"
                  >
                    추가결제
                  </button>
                  <button
                    onClick={() => setFulfillTarget(c)}
                    className="rounded-lg border border-emerald-500 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-50"
                  >
                    대체발송
                  </button>
                  <button
                    onClick={() => handleCancel(c._id)}
                    disabled={cancelingId === c._id}
                    className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    {cancelingId === c._id ? '취소중…' : '취소'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {payTarget && (
        <ExtraPayModal
          couponId={payTarget._id}
          couponCode={payTarget.code}
          productLabel={productLabel(payTarget)}
          onPay={onPay}
          onClose={() => setPayTarget(null)}
        />
      )}
      {fulfillTarget && (
        <ExtraFulfillModal
          couponId={fulfillTarget._id}
          couponCode={fulfillTarget.code}
          productLabel={productLabel(fulfillTarget)}
          getDonors={getDonors}
          onFulfill={onFulfill}
          onClose={() => setFulfillTarget(null)}
        />
      )}
    </div>
  );
};
